import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from './authentication.service';
import { Constants } from '../interfaces/constants';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Router } from '@angular/router';

@Injectable({
	providedIn: 'root',
})
export class UtilsService {
	private renderer: Renderer2;
	private darkModeActive = false;
	private isClientRole = false;
	currentLanguage = 'mk';

	public VALIDATION_PATTERNS: Map<string, string> = new Map([
		//['URL', '(http(s)?:\/\/.)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .%-?\\#]*/?'],
		['URL', 'https?://.*?(?:s|$)'],
		['NUMBER_LIST', '^[0-9,]+$'],
		['E_MAIL', '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$'],
	]);

	public readonly CHIPS_SEPERATOR_KEYS = [ENTER, COMMA];

	constructor(rendererFactory: RendererFactory2, private translateService: TranslateService, private authentication: AuthenticationService, private router: Router) {
		this.renderer = rendererFactory.createRenderer(null, null);

		const storedTheme = this.localStorageManagement('theme', this.darkModeActive);
		this.darkModeActive = storedTheme === 'true' || storedTheme === true;
		this.applyThemeClass();

		if (this.currentLanguage !== this.localStorageManagement('language', this.currentLanguage)) {
			this.languageManagement(this.localStorageManagement('language', this.currentLanguage));
		}

		// Clients never get the dark theme (CLIPPING-600) - this stays reactive to role changes
		// (not just read once at app bootstrap) so switching role in the same browser tab, in
		// either direction, re-applies the right body class without touching the Admin's saved
		// preference in localStorage.
		this.authentication.loginData.subscribe(loginData => {
			this.isClientRole = loginData?.role === 'CLIENT';
			this.applyThemeClass();
		});
	}

	private applyThemeClass() {
		if (this.isClientRole || !this.darkModeActive) {
			this.renderer.removeClass(document.body, 'dark-theme');
		} else {
			this.renderer.addClass(document.body, 'dark-theme');
		}
	}

	themeManagement(change?: boolean) {
		if (change && !this.isClientRole) {
			this.darkModeActive = !this.darkModeActive;
			localStorage.setItem('theme', JSON.stringify(this.darkModeActive));
			this.applyThemeClass();
		}
		return !this.isClientRole && this.darkModeActive;
	}

	languageManagement(lang?: string) {
		if (lang) {
			this.translateService.use(lang);
			this.currentLanguage = lang;
			localStorage.setItem('language', this.currentLanguage);
		}
		return this.currentLanguage;
	}

	localStorageManagement(key: string, defaultValue: any) {
		const storedValue = localStorage.getItem(key);

		if (storedValue === undefined || storedValue === null) {
			localStorage.setItem(key, defaultValue);
			return defaultValue;
		}

		return storedValue;
	}

	getErrorMessage(formControl: AbstractControl) {
		if (formControl.hasError('required')) {
			return 'NOTIFICATIONS.REQUIRED_VALUE';
		}
		if (formControl.hasError('pattern')) {
			return 'NOTIFICATIONS.PATTERN_NOT_MATCHED';
		}

		return formControl.hasError('email') ? 'Not a valid email' : '';
	}

	formatDate(date: any, includeTime?: boolean) {
		let dateConverted = new Date(date);
		if (dateConverted instanceof Date) {
			let parsedDate = `${dateConverted.getDate().toString().padStart(2, '0')}.${(dateConverted.getMonth() + 1).toString().padStart(2, '0')}.${dateConverted.getFullYear()}`;
			if (includeTime) {
				parsedDate += ` ${dateConverted.getHours().toString().padStart(2, '0')}:${dateConverted.getMinutes().toString().padStart(2, '0')}:${dateConverted
					.getSeconds()
					.toString()
					.padStart(2, '0')}`;
			}
			return parsedDate;
		} else {
			return date;
		}
	}

	timestampToDateFormatted(timestamp: string) {
		const date = new Date(timestamp);

		const year = date.getUTCFullYear();
		const month = String(date.getUTCMonth() + 1).padStart(2, '0');
		const day = String(date.getUTCDate()).padStart(2, '0');

		return `${year}-${month}-${day}`;
	}

	getParameterByLanguageScript(item: any, paramName: string, withDetails?: boolean) {
		if (withDetails) {
			if (!item || !item.details) {
				return;
			}
			const detailsByCode = item.details.find((detailsItem: any) => detailsItem.lang.toLowerCase() === this.currentLanguage);
			if (detailsByCode && detailsByCode[paramName]) {
				return detailsByCode[paramName];
			}

			if (item.medium?.lang) {
				const mediumLangCode = item.medium.lang.toLowerCase();
				const detailsByMediumLang = item.details.find((detailsItem: any) => detailsItem.lang.toLowerCase() === mediumLangCode);
				if (detailsByMediumLang && detailsByMediumLang[paramName]) {
					return detailsByMediumLang[paramName];
				}
			}

			if (item.details.length > 0) {
				return item.details[0][paramName];
			}
		}
		return item[paramName + Constants.AVAILABLE_LANGUAGES.find(lang => lang.code === this.currentLanguage)?.script];
	}

	getParameterByName(item: any, paramName: string) {
		return item[paramName] ? item[paramName] : undefined;
	}

	suppressType(item: any) {
		return item as any;
	}

	compareSuppressNulls(item: any, itemToCompareWith: any) {
		item = item === undefined || item === null ? null : item;
		itemToCompareWith = itemToCompareWith === undefined || itemToCompareWith === null ? null : itemToCompareWith;

		return item === itemToCompareWith;
	}

	convertToMiliseconds(unit: 'DAY' | 'HOUR' | 'MINUTE' | 'SECOND', value: number): number {
		switch (unit) {
			case 'DAY': {
				return this.convertToMiliseconds('HOUR', value * 24);
			}
			case 'HOUR': {
				return this.convertToMiliseconds('MINUTE', value * 60);
			}
			case 'MINUTE': {
				return this.convertToMiliseconds('SECOND', value * 60);
			}
			case 'SECOND': {
				return value * 1000;
			}
		}
	}

	setDateToDay(date: Date, period: 'START' | 'END' | 'CURRENT_TIME') {
		if (date) {
			const dateStartOfDay = new Date((date as Date).setHours(0, 0, 0, 0)).getTime();
			switch (period) {
				case 'START': {
					return dateStartOfDay;
				}
				case 'END': {
					return new Date(new Date(dateStartOfDay).setDate(new Date(dateStartOfDay).getDate() + 1)).getTime();
				}
				case 'CURRENT_TIME': {
					const currentDate = new Date();
					return new Date(new Date(dateStartOfDay).setHours(currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds())).getTime();
				}
			}
		}
		return date;
	}

	formatCurrency(value: string) {
		if (value) {
			const [integerPart, decimalPart] = parseInt(value, 10).toFixed(2).split('.');
			const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
			return `${formattedInteger},${decimalPart}`;
		}
		return '';
	}

	formatCurrencyLocale(value: number) {
		if (value) {
			return value.toLocaleString('de-DE');
		}
		return '0';
	}

	getLanguageByCode(code: string) {
		return Constants.AVAILABLE_LANGUAGES.find(item => item.code === code.toLowerCase());
	}

	removeObjectParameterByName(item: any, paramName: string) {
		if (item.hasOwnProperty(paramName)) {
			delete item[paramName];
		}
		return item;
	}

	validateRequestObject(item: any) {
		let validatedObject = item;
		validatedObject = this.removeObjectValuesLeadingTrailingSpaces(item);
		// other validation methods
		return validatedObject;
	}

	private removeObjectValuesLeadingTrailingSpaces(item: any) {
		Object.keys(item).forEach(key => {
			if (item[key] && typeof item[key] === 'string') {
				item[key] = item[key].trim();
			}
		});
		return item;
	}

	titleCase(text: string) {
		return text
			.split(' ')
			.map((part: string) => part[0].toUpperCase() + part.substr(1))
			.join(' ');
	}

	truncateText(text: string, length: number) {
		return text && text.length > length ? text.substring(0, length) + '...' : text;
	}

	splitTextToList(text: string, separator: string): string[] {
		return text ? text.split(separator) : [''];
	}

	transliterate(conversion: 'CYR_LAT' | 'LAT_CYR', word: string) {
		const cyrillicToLatin: Map<string, string> = new Map([
			['А', 'A'],
			['а', 'a'],
			['Б', 'B'],
			['б', 'b'],
			['В', 'V'],
			['в', 'v'],
			['Г', 'G'],
			['г', 'g'],
			['Д', 'D'],
			['д', 'd'],
			['Ѓ', 'GJ'],
			['ѓ', 'gj'],
			['Е', 'E'],
			['е', 'e'],
			['Ж', 'ZH'],
			['ж', 'zh'],
			['З', 'Z'],
			['з', 'z'],
			['Ѕ', 'DZ'],
			['ѕ', 'dz'],
			['И', 'I'],
			['и', 'i'],
			['Ј', 'J'],
			['ј', 'j'],
			['К', 'K'],
			['к', 'k'],
			['Л', 'L'],
			['л', 'l'],
			['Љ', 'LJ'],
			['љ', 'lj'],
			['М', 'M'],
			['м', 'm'],
			['Н', 'N'],
			['н', 'n'],
			['Њ', 'NJ'],
			['њ', 'nj'],
			['О', 'O'],
			['о', 'o'],
			['П', 'P'],
			['п', 'p'],
			['Р', 'R'],
			['р', 'r'],
			['С', 'S'],
			['с', 's'],
			['Т', 'T'],
			['т', 't'],
			['Ќ', 'KJ'],
			['ќ', 'kj'],
			['У', 'Y'],
			['у', 'y'],
			['Ф', 'F'],
			['ф', 'f'],
			['Х', 'H'],
			['х', 'h'],
			['Ц', 'TC'],
			['ц', 'ts'],
			['Ч', 'CH'],
			['ч', 'ch'],
			['Џ', 'X'],
			['џ', 'x'],
			['Ш', 'SH'],
			['ш', 'sh'],
		]);
		const latinToCyrillic: Map<string, string> = new Map([
			['A', 'А'],
			['a', 'а'],
			['B', 'Б'],
			['b', 'б'],
			['C', 'Ц'],
			['c', 'ц'],
			['D', 'Д'],
			['d', 'д'],
			['E', 'Е'],
			['e', 'е'],
			['F', 'Ф'],
			['f', 'ф'],
			['G', 'Г'],
			['g', 'г'],
			['H', 'Х'],
			['h', 'х'],
			['I', 'И'],
			['i', 'и'],
			['J', 'Ј'],
			['j', 'ј'],
			['K', 'К'],
			['k', 'к'],
			['L', 'Л'],
			['l', 'л'],
			['M', 'М'],
			['m', 'м'],
			['N', 'Н'],
			['n', 'н'],
			['O', 'О'],
			['o', 'о'],
			['P', 'П'],
			['p', 'п'],
			['Q', 'Љ'],
			['q', 'љ'],
			['R', 'Р'],
			['r', 'р'],
			['S', 'С'],
			['s', 'с'],
			['T', 'Т'],
			['t', 'т'],
			['U', 'У'],
			['u', 'у'],
			['V', 'В'],
			['v', 'в'],
			['W', 'В'],
			['w', 'в'],
			['X', 'Џ'],
			['x', 'џ'],
			['Y', 'у'],
			['y', 'у'],
			['Z', 'З'],
			['z', 'з'],
		]);
		let conversionPattern = conversion === 'CYR_LAT' ? cyrillicToLatin : latinToCyrillic;

		return word
			.split('')
			.map((char: any) => {
				return conversionPattern.get(char) ? conversionPattern.get(char) : char;
			})
			.join('');
	}

	getQueryParamsFromStringUrl(url: string) {
		const regex = /[?&]([^=#]+)=([^&#]*)/g;
		let params: any = {};
		let match;
		while ((match = regex.exec(url))) {
			params[decodeURIComponent(match[1])] = decodeURIComponent(match[2]);
		}
		return params;
	}

	public matchValidator(controlName: string, matchingControlName: string): ValidatorFn {
		return (abstractControl: AbstractControl) => {
			const control = abstractControl.get(controlName);
			const matchingControl = abstractControl.get(matchingControlName);

			if (matchingControl!.errors && !matchingControl!.errors?.['confirmedValidator']) {
				return null;
			}

			if (control!.value !== matchingControl!.value) {
				const error = { confirmedValidator: 'Passwords do not match.' };
				matchingControl!.setErrors(error);
				return error;
			} else {
				matchingControl!.setErrors(null);
				return null;
			}
		};
	}

	openLinkInNewTab(url: string) {
		if (url && url !== 'null' && url.trim() !== '') {
			window.open(url, '_blank');
		}
	}

	navigateToPage(routerLink: string) {
		this.router.navigate([routerLink]);
	}

	getScssVarValue(varName: string) {
		return window.getComputedStyle(document.documentElement).getPropertyValue(`--${varName}`);
	}

	getIconByType(type: string) {
		switch (type) {
			case 'MEDIUM_TV': {
				return 'tv';
			}
			case 'MEDIUM_RADIO': {
				return 'radio';
			}
			case 'MEDIUM_PRINTED': {
				return 'list_alt';
			}
			default: {
				return 'public';
			}
		}
	}

	autoConvertLatinToCyrillic(text: string): string {
		return text
			.split('')
			.map((char: string) => {
				if (/[\u0400-\u04FF]/.test(char)) {
					return char;
				}
				const converted = this.transliterate('LAT_CYR', char);
				return converted;
			})
			.join('');
	}
}
